import { useState, useEffect, useRef } from 'react';

/**
 * Hook that ensures loading state stays true for a minimum duration.
 * Prevents "flash" of loading state when requests are very fast.
 * 
 * @param isLoading - The actual loading state from a query/mutation
 * @param minTime - Minimum time in ms to show loading (default: 400ms)
 */
export const useMinLoadingTime = (isLoading: boolean, minTime = 400): boolean => {
  const [showLoading, setShowLoading] = useState(false);
  const loadingStartTime = useRef<number | null>(null);

  useEffect(() => {
    if (isLoading) {
      // Loading started
      loadingStartTime.current = Date.now();
      setShowLoading(true);
      return undefined;
    }
    
    if (loadingStartTime.current === null) {
      return undefined;
    }

    // Loading finished - check if we need to wait
    const elapsed = Date.now() - loadingStartTime.current;
    const remaining = minTime - elapsed;

    if (remaining > 0) {
      // Wait for remaining time before hiding loading
      const timer = setTimeout(() => {
        setShowLoading(false);
        loadingStartTime.current = null;
      }, remaining);
      return () => clearTimeout(timer);
    }
    
    // Already waited enough
    setShowLoading(false);
    loadingStartTime.current = null;
    return undefined;
  }, [isLoading, minTime]);

  return showLoading;
};


